import {Phone, Calendar, Shield, Sparkles, Car, Award, Check, Zap, MapPin} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const PpfSanMarcosPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does paint protection film cost in San Marcos?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Pricing depends on vehicle type and paint condition."
          }
        },
        {
          "@type": "Question",
          "name": "Does PPF protect against Highway 78 rock chips?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. PPF provides essential defense against rock chips, gravel spray, and debris common on Highway 78 and I-15. Film absorbs impacts that would otherwise chip paint permanently."
          }
        },
        {
          "@type": "Question",
          "name": "How long does PPF installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front takes 1 day, full front requires 2 days, and full vehicle installation takes 3-5 days depending on vehicle size and coverage."
          }
        },
        {
          "@type": "Question",
          "name": "Is PPF good for trucks and work vehicles in San Marcos?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. PPF is ideal for work trucks, commercial vehicles, and SUVs used in San Marcos business and contracting industries. Protects against job site debris, tool scratches, and daily wear."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Paint Protection Film San Marcos | Professional PPF Installation Near CSUSM"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert paint protection film installation in San Marcos, CA. Protect trucks, SUVs, Tesla with professional PPF services. Self-healing technology, manufacturer-backed film warranties. Serving San Marcos, Twin Oaks, and North County inland.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/ppf-san-marcos" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Paint Protection Film in San Marcos, CA</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your vehicle with professional paint protection film installation serving San Marcos, California. Our installers provide premium PPF coverage for trucks, SUVs, Tesla, and work vehicles—delivering durable protection against Highway 78 rock chips, job site debris, and daily commuting damage throughout San Marcos and North County inland.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving San Marcos, Twin Oaks Valley, Discovery Hills, and CSUSM area residents with bulk or precision-pattern installation, self-healing technology, and manufacturer-backed film warranties. Conveniently located in Vista, just 10 minutes from San Marcos Boulevard via Highway 78, we offer flexible scheduling for families, students, and professionals who need reliable paint protection for daily drivers and work vehicles.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Vehicle Protection */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Luxury Vehicle Paint Protection in San Marcos</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              San Marcos's growing professional community and CSUSM faculty drive premium vehicles requiring expert paint protection. We specialize in PPF installation for Porsche Cayenne, Macan, and 911 models popular among San Marcos executives and professionals. BMW X5, X7, and luxury sedan owners trust our installers for precision coverage protecting German engineering from Highway 78 debris and North County San Diego inland heat.
            </p>
            <p>
              Mercedes-Benz vehicles including GLE, GLS, E-Class, and AMG variants driven throughout Discovery Hills and Twin Oaks Valley receive professional paint protection at our nearby Vista facility. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation</a> protects Model 3, Model Y, Model S, and Model X vehicles popular with San Marcos families, CSUSM faculty, and environmentally-conscious professionals commuting to Carlsbad and Vista business parks. Audi Q7, Q8, and premium sedan owners benefit from our expertise with European paint systems.
            </p>
            <p>
              Range Rover, Lexus, and premium SUVs driven in San Marcos require <a href="/paint-protection-film" className="text-cyan-400 hover:underline">professional paint protection</a> against Highway 78's harsh commuting conditions and inland UV exposure. Our Vista location—just 10 minutes from San Marcos Boulevard—offers convenient access for luxury vehicle owners throughout North County San Diego inland communities seeking professional installation quality and manufacturer-backed warranty protection.
            </p>
          </div>
        </div>
      </section>

      {/* Why San Marcos Needs PPF */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why San Marcos Vehicle Owners Choose Paint Protection Film</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              San Marcos sits at the crossroads of Highway 78 and I-15, creating intense rock chip exposure for vehicles commuting throughout North County. Daily traffic on Highway 78 between Oceanside and Escondido includes construction trucks, gravel haulers, and commercial vehicles that constantly spray debris across all lanes. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> prevents the cumulative paint damage that occurs from San Marcos highway commuting, protecting your investment and maintaining factory appearance despite harsh driving conditions.
            </p>
            <p>
              San Marcos vehicle owners drive practical trucks and SUVs alongside family sedans and electric vehicles—Ford F-150 and Super Duty trucks used by local contractors, Chevrolet Silverado and Tahoe, RAM trucks, Toyota Tacoma and 4Runner, Tesla Model 3 and Model Y popular with CSUSM faculty and professionals, Honda Accord and CR-V, Subaru Outback, and Jeep Wrangler. These vehicles serve real-world transportation needs, making paint protection essential for maintaining appearance and value through years of San Marcos daily driving.
            </p>
            <p>
              San Marcos Boulevard, Rancho Santa Fe Road, and Twin Oaks Valley Road carry heavy traffic connecting residential areas to employment centers, Cal State San Marcos campus, shopping districts, and I-15 access. These busy arterial roads experience constant vehicle traffic and debris, creating paint damage risks beyond highway commuting. Professional PPF installation protects against the varied impact damage that occurs during typical San Marcos daily driving routines.
            </p>
            <p>
              San Marcos's inland climate means summer temperatures routinely exceeding 85°F and intense UV exposure year-round. Vehicles parked at Westfield North County shopping center, Cal State San Marcos parking structures, or neighborhood driveways experience relentless sun damage that causes paint fading, clear coat oxidation, and chalking. Premium PPF includes UV-resistant properties that prevent solar damage while maintaining optical clarity, essential for preserving vehicle appearance in San Marcos's hot, dry environment.
            </p>
            <p>
              Our <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation services</a> protect the growing number of Tesla vehicles driven by San Marcos professionals, CSUSM faculty, and families. Tesla's thin paint requires specialized protection against Highway 78 rock chips and San Marcos parking lot damage. We install model-specific patterns for Model 3, Model Y, Model S, and Model X with precision coverage that maintains Autopilot functionality and factory warranty compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF Coverage Packages for San Marcos Vehicles</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Coverage</h3>
              <p className="text-gray-300 mb-6">
                Essential protection for high-impact zones: front bumper, partial hood, mirrors, headlights. Ideal for daily commuters on Highway 78.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper full coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Mirrors and headlights</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Coverage</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive front-end protection: full hood, bumper, fenders, mirrors, headlights. Maximum highway protection.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, and bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Headlights, fog lights, mirrors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Coverage</h3>
              <p className="text-gray-300 mb-6">
                Ultimate protection for every painted surface. Maintain showroom condition and maximize resale value.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rocker panels, quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            Starting prices shown are for standard vehicles. Exact pricing determined after free consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Premium PPF Installation for San Marcos Drivers</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Highway Defense</h3>
              <p className="text-gray-300">Protection against Highway 78 and I-15 rock chips, gravel spray, and construction debris from San Marcos commuting.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Film</h3>
              <p className="text-gray-300">Advanced technology repairs minor scratches with heat—perfect for San Marcos parking lots and daily use.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Work Vehicle Expertise</h3>
              <p className="text-gray-300">Specialized installation for trucks, vans, and commercial vehicles used in San Marcos business community.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Manufacturer-Backed Warranty</h3>
              <p className="text-gray-300">Manufacturer warranty against yellowing, cracking, peeling, plus workmanship guarantee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tesla PPF Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Paint Protection Film for San Marcos Owners</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg mb-12">
            <p>
              San Marcos has growing Tesla ownership, particularly among CSUSM faculty, professionals working in Carlsbad and Vista business parks, and families attracted to the city's suburban amenities. Tesla's water-based paint requires professional protection against Highway 78 rock chips and San Marcos parking lot damage. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation</a> preserves factory finish and prevents the paint degradation that occurs from daily commuting on North County's busiest highway corridors.
            </p>
            <p>
              We install model-specific PPF patterns designed for Model 3, Model Y, Model S, Model X, and Cybertruck. Bulk or precision-pattern methods keep coverage complete across Tesla's unique design features including flush door handles, charging ports, and sensor arrays. Our installation process protects all Autopilot hardware while delivering seamless edge-to-edge coverage invisible to the eye. Many San Marcos Tesla owners schedule installation immediately after delivery to protect against Highway 78's notorious rock chip conditions.
            </p>
            <p>
              Popular packages for San Marcos Tesla owners include full front coverage (starting at $1,899) for comprehensive highway protection, and partial front coverage (starting at $1,299) for budget-conscious owners who primarily need bumper and hood protection. We also offer <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> services that complement Tesla PPF for complete protection against San Marcos's intense inland heat and UV exposure.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Model-Specific Templates</h3>
              <p className="text-gray-300">Precision-cut patterns for every Tesla model ensure flawless fitment without blade work on paint.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Sensor Protection</h3>
              <p className="text-gray-300">Proper masking around Autopilot hardware ensures full Tesla technology functionality with complete paint protection.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Factory Warranty Safe</h3>
              <p className="text-gray-300">Professional PPF doesn't void Tesla warranty—it helps maintain warranty-compliant paint condition.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Professional PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Vehicle Assessment</h3>
                <p className="text-gray-300">We inspect your vehicle's paint condition, discuss your protection needs, and provide a detailed quote. For San Marcos customers, we review commute routes, parking situations, and vehicle usage to recommend appropriate PPF coverage that fits your budget and protection goals.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Decontamination</h3>
                <p className="text-gray-300">Thorough wash, clay bar treatment, and paint decontamination remove embedded debris, tar spots, and surface contaminants common on San Marcos vehicles. For used vehicles with existing damage, we perform multi-stage paint correction to eliminate swirl marks and light scratches before PPF application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation, Selected by Vehicle and Panel</h3>
                <p className="text-gray-300">Using vehicle-specific templates, we apply premium self-healing PPF with professional wet installation techniques in our indoor Vista facility. Proper squeegee methods, edge wrapping, and heat forming achieve seamless coverage across complex body lines and contours without visible seams or film edges.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Delivery</h3>
                <p className="text-gray-300">Final multi-point inspection verifies perfect edges, complete adhesion, and optical clarity. We provide comprehensive care instructions, warranty documentation, and long-term maintenance recommendations. Your vehicle returns to San Marcos with invisible protection and showroom appearance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Context */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Paint Protection for San Marcos's Driving Conditions</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Highway 78 between Oceanside and Escondido is one of North County's busiest commuter corridors, carrying thousands of vehicles daily including heavy construction trucks, gravel haulers, and commercial traffic. San Marcos drivers commuting east to Escondido or west toward Oceanside and Carlsbad face relentless rock chip bombardment. Unprotected vehicles accumulate dozens of paint chips within months of regular Highway 78 commuting. Professional PPF installation eliminates this damage, creating a sacrificial barrier that absorbs impacts and maintains factory paint integrity.
            </p>
            <p>
              I-15 access via Pomerado Road connects San Marcos to Rancho Bernardo, Scripps Ranch, Mira Mesa, and downtown San Diego. This major freeway carries constant traffic at high speeds, maximizing rock chip velocity and impact damage. Debris from construction projects along the I-15 corridor creates additional hazards. PPF protects against these varied highway threats, essential for San Marcos residents who commute daily on North County's busiest freeways.
            </p>
            <p>
              San Marcos Boulevard, Rancho Santa Fe Road, and Twin Oaks Valley Road experience heavy local traffic connecting residential neighborhoods to Cal State San Marcos, shopping centers, and business parks. These arterial roads accumulate debris, gravel, and contaminants that cause paint damage during stop-and-go traffic. PPF prevents the cumulative wear from daily San Marcos driving, maintaining vehicle appearance despite constant road exposure.
            </p>
            <p>
              Parking at Westfield North County Fair, Regal San Marcos Stadium, CSUSM campus lots, and San Marcos Town Center subjects vehicles to door dings, shopping cart impacts, and careless pedestrian contact. Self-healing PPF technology repairs light scratches from these parking lot encounters, ideal for high-traffic shopping and campus environments. The film absorbs minor impacts that would otherwise cause permanent paint chips or dents requiring body shop repair.
            </p>
            <p>
              San Marcos's inland climate means intense summer heat, minimal coastal breeze, and year-round UV exposure. Vehicles parked outdoors experience accelerated paint fading, clear coat oxidation, and sun damage that plague inland Southern California communities. Premium PPF includes UV-resistant properties that prevent solar degradation while maintaining optical clarity for 10+ years. The hydrophobic top coat also repels water and dirt, making vehicle maintenance easier in San Marcos's dry, dusty environment. Professional PPF installation delivers long-term value for San Marcos vehicle owners who need durable protection against real-world driving conditions and environmental exposure.
          </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">San Marcos PPF FAQs</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does paint protection film cost in San Marcos?</h3>
              <p className="text-gray-300">Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Pricing depends on vehicle type and paint condition. We provide detailed quotes for San Marcos residents with transparent pricing and no hidden fees.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF protect against Highway 78 rock chips?</h3>
              <p className="text-gray-300">Yes. PPF provides essential defense against rock chips, gravel spray, and debris common on Highway 78 between Oceanside and Escondido. The film absorbs impacts that would otherwise create permanent paint chips, protecting your vehicle during daily San Marcos highway commuting.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF installation take?</h3>
              <p className="text-gray-300">Partial front takes 1 day, full front requires 2 days, and full vehicle installation takes 3-5 days depending on vehicle size and coverage areas. Larger trucks and SUVs may require additional time for complete panel coverage.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF good for trucks and work vehicles in San Marcos?</h3>
              <p className="text-gray-300">Absolutely. PPF is ideal for work trucks, commercial vehicles, and SUVs used in San Marcos business and contracting industries. Protects against job site debris, tool scratches, equipment contact, and daily commercial use wear that reduces vehicle appearance and resale value.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF last in San Marcos's climate?</h3>
              <p className="text-gray-300">Premium PPF lasts 10+ years in San Marcos's inland climate. UV resistance prevents sun damage and fading despite year-round heat exposure. Self-healing properties remain active throughout the film's lifespan, repairing minor scratches as they occur.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be applied to fleet vehicles?</h3>
              <p className="text-gray-300">Yes. We offer fleet PPF installation for San Marcos businesses protecting multiple vehicles. Fleet pricing available for contractors, delivery services, and companies maintaining professional vehicle appearance for customer-facing operations.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF damage my paint when removed?</h3>
              <p className="text-gray-300">No. Professional PPF removal leaves no adhesive residue or paint damage. After 10+ years, removing PPF reveals factory-fresh paint underneath while unprotected areas show significant wear, proving the film's protective value.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do you offer warranty coverage for San Marcos customers?</h3>
              <p className="text-gray-300">Yes. All installations include 10-year manufacturer warranty against defects plus our workmanship guarantee. We service warranty claims promptly for San Marcos customers, fixing any bubbling, lifting, or adhesion issues at no charge during the warranty period.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Comprehensive vehicle protection for San Marcos drivers:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/paint-protection-film-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Full PPF pricing, coverage options, and installation guide for San Diego</p>
            </a>
            <a href="/tesla-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Specialized PPF for all Tesla models with precision installation</p>
            </a>
            <a href="/ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">PPF San Diego</h3>
              <p className="text-gray-400 text-sm">San Diego paint protection film services for all vehicles</p>
            </a>
            <a href="/commercial-fleet-wraps" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Commercial Fleet Wraps</h3>
              <p className="text-gray-400 text-sm">Fleet graphics and protection for San Marcos business vehicles</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Serving San Marcos & North County Inland Communities
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in Vista, just 10 minutes from San Marcos via Highway 78. We serve vehicle owners throughout San Marcos and North County inland with professional PPF installation.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">San Marcos Areas</h3>
              <ul className="space-y-1 text-sm">
                <li>• Discovery Hills</li>
                <li>• Twin Oaks Valley</li>
                <li>• San Elijo Hills</li>
                <li>• CSUSM Area</li>
                <li>• Lake San Marcos</li>
                <li>• Richland</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Nearby Cities</h3>
              <ul className="space-y-1 text-sm">
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> (Shop Location)</li>
                <li>• <a href="/ppf-escondido" className="text-cyan-400 hover:underline">Escondido</a></li>
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-poway" className="text-cyan-400 hover:underline">Poway</a></li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your San Marcos Vehicle with Professional PPF</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free consultation and get a custom quote for paint protection film installation. Same-week appointments available for San Marcos residents.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default PpfSanMarcosPage
